import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import get from 'lodash/get';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import Dialog from '@lightspeed/cirrus-dialog';
import zipObject from 'lodash/zipObject';

import history from 'pages/router/history';
import { entities } from 'pages/store/schema';
import { getData, putData, clearData, deleteData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import {
  getProcessing,
  getErrors,
  getGatewayConfigurationParameters,
  getAvailableGatewayTypes,
  getGatewayCredentialParameters,
  getMappablePaymentTypes,
  getGatewayCardMappings,
  getCCTerminalByGatewayConfigID,
  getValue,
  getValues,
} from 'pages/store/selectors';

import Layout, { LayoutSection } from 'cirrus/Layout';
import ViewError from 'pages/components/ViewError';

import GatewayType from '../GatewayType';
import GatewayName from '../GatewayName';
import SelectedShops from '../SelectedShops';
import GatewayCredentials from '../GatewayCredentials';
import GatewayConfiguration from '../GatewayConfiguration';
import PaymentTypeMapping from '../PaymentTypeMapping';
import { paths } from '../../routes';

const merchantos = window.merchantos;

const STRINGS = {
  SELECT_GATEWAY: {
    TITLE: 'Select Gateway',
    DESCRIPTION: 'Select your payment processor',
  },
  GATEWAY_NAME: {
    TITLE: 'Assign a Name',
    DESCRIPTION: 'You may assign a unique name to this gateway configuration',
  },
  GATEWAY_CREDENTIALS: {
    TITLE: 'Gateway Credentials',
    DESCRIPTION: 'You can get this info from your payment processor',
  },
  SELECTED_SHOPS: {
    TITLE: 'Select Shops',
    DESCRIPTION: 'Select the shops that will be using this gateway to process payments',
  },
  PAYMENT_MAPPING: {
    TITLE: 'Credit Card Payment Mapping',
    DESCRIPTION: 'Select how card brands map to your payment types',
  },
  ERROR: {
    SAVE_ERROR: {
      TITLE: 'Gateway Configuration not Saved',
      MESSAGE:
        'The gateway configuration could not be saved.  If this problem persists, please contact customer support.',
    },
    SAVE_CREDENTIALS_ERROR: {
      MESSAGE: 'Please fill out all of the required form fields.',
    },
    DELETE_ERROR: {
      TITLE: 'Gateway Configuration not Deleted',
      MESSAGE:
        'The gateway configuration could not be removed.  If this problem persists, please contact customer support.',
    },
  },
  DIALOG: {
    DELETE: {
      TITLE: 'Delete Gateway?',
      MESSAGE:
        'Are you sure you wish to delete this Payment Gateway?\n\nAll settings will be lost.',
    },
  },
};

// populate Formik field values
export const formatInitialValues = ({
  gatewayConfig = {},
  gatewayCredentialParameters = [],
  gatewayConfigurationParameters = [],
  cardMappings = [],
  shops = [],
}) => {
  const gatewayCredentials = zipObject(
    gatewayCredentialParameters.map(parameter => parameter.id),
    gatewayCredentialParameters.map(parameter => get(gatewayConfig, `parameters.${parameter.id}`))
  );

  const gatewayConfiguration = zipObject(
    gatewayConfigurationParameters.map(parameter => parameter.id),
    gatewayConfigurationParameters.map(parameter => {
      const value = get(gatewayConfig, `parameters.${parameter.id}`);

      // current API doesn't properly report boolean types so we need to convert them
      return value === 'true' || value === 'false' ? value === 'true' : value;
    })
  );

  const cardSchemeMappings = zipObject(
    cardMappings.map(mapping => `cardMapping-${get(mapping, 'cardSchemeID')}`),
    cardMappings.map(mapping => get(mapping, 'paymentTypeID'))
  );

  const selectedShops = zipObject(
    shops.map(shop => `shop_${get(shop, 'shopID')}`),
    shops.map(shop => get(shop, 'gatewayConfigID') === get(gatewayConfig, 'gatewayConfigID'))
  );

  return {
    gatewayID: get(gatewayConfig, 'gatewayID'),
    gatewayName: get(gatewayConfig, 'name', ''),
    ...gatewayCredentials,
    ...selectedShops,
    ...gatewayConfiguration,
    ...cardSchemeMappings,
  };
};

class EditGatewayComponent extends Component {
  constructor(props) {
    super(props);
    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
            icon: 'settings',
          },
          {
            text: 'Payment Processing',
            page: paths.paymentProcessing,
          },
          {
            text: 'Edit Payment Gateway',
          },
        ],
      },
    });

    this.state = {
      openDeleteDialog: false,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleDeleteButtonClicked = this.handleDeleteButtonClicked.bind(this);
    this.renderGatewayCredentials = this.renderGatewayCredentials.bind(this);
  }

  componentDidMount() {
    this.props.getData();
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleSave(values = {}) {
    const {
      gatewayConfig,
      gatewayConfigurationParameters,
      gatewayCredentialParameters,
      cardMappings,
      shops,
    } = this.props;
    const { gatewayConfigID, gatewayID } = gatewayConfig;
    const { gatewayName } = values;

    // Formik requires a flat value structure so we need to prepare our data for transmission
    const gatewayCredentials = {};
    gatewayCredentialParameters.forEach(parameter => {
      gatewayCredentials[parameter.id] = get(values, parameter.id);
    });

    const gatewayConfiguration = {};
    gatewayConfigurationParameters.forEach(parameter => {
      gatewayConfiguration[parameter.id] = get(values, parameter.id);
    });

    const cardSchemeMappings = cardMappings.map(mapping => {
      const newPaymentTypeID = get(values, `cardMapping-${get(mapping, 'cardSchemeID')}`);

      return {
        ...mapping,
        paymentTypeID: newPaymentTypeID,
      };
    });

    const selectedShops = shops.map(shop => {
      const selectedToThisShop = get(values, `shop_${get(shop, 'shopID')}`);
      const wasSelectedToThisShop =
        get(shop, 'gatewayConfigID') === get(gatewayConfig, 'gatewayConfigID');

      let newGatewayConfigID = get(shop, 'gatewayConfigID');
      if (selectedToThisShop) {
        newGatewayConfigID = gatewayConfigID;
      } else if (!selectedToThisShop && wasSelectedToThisShop) {
        newGatewayConfigID = '0';
      }

      return {
        ...shop,
        gatewayConfigID: newGatewayConfigID,
      };
    });

    const payload = {
      gatewayConfigID,
      gatewayID,
      name: gatewayName,
      cardSchemeMappings: {
        CardSchemeMapping: cardSchemeMappings,
      },
      parameters: {
        ...gatewayCredentials,
        ...gatewayConfiguration,
      },
    };

    if (gatewayConfigID) {
      this.props.saveData(gatewayConfigID, payload, selectedShops);

      selectedShops.forEach(shop => this.props.saveShopData(shop.shopID, shop));
    }
  }

  handleDelete() {
    const { deleteGateway, gatewayConfig } = this.props;

    this.setState(
      {
        openDeleteDialog: false,
      },
      () => {
        deleteGateway(get(gatewayConfig, 'gatewayConfigID'), () => {
          history.push(paths.paymentProcessing);
        });
      }
    );
  }

  handleDeleteButtonClicked() {
    this.setState({
      openDeleteDialog: true,
    });
  }

  renderGatewayCredentials() {
    const { isLoading, gatewayCredentialParameters } = this.props;

    return (
      <LayoutSection
        title={STRINGS.GATEWAY_CREDENTIALS.TITLE}
        description={STRINGS.GATEWAY_CREDENTIALS.DESCRIPTION}
      >
        <GatewayCredentials isLoading={isLoading} parameters={gatewayCredentialParameters} />
      </LayoutSection>
    );
  }

  render() {
    const {
      availableGatewayTypes,
      isLoading,
      isSaving,
      isErroring,
      isDeleting,
      gatewayCredentialParameters,
      gatewayConfigurationParameters,
      paymentTypes,
      cardMappings,
      saveErrors,
      deleteErrors,
      shops,
      match,
    } = this.props;
    const { openDeleteDialog } = this.state;

    const initialValues = formatInitialValues(this.props);
    const gatewayId = get(match, 'params.gatewayId');

    return (
      <ViewError isErroring={isErroring}>
        <Formik
          enableReinitialize
          onSubmit={this.handleSave}
          initialValues={initialValues}
          render={formikProps => {
            const { dirty, handleSubmit } = formikProps;
            return (
              <Fragment>
                <div className="cr-flex cr-justify-between cr-items-center cr-width-full cr-p-2 ">
                  <Button
                    variant="secondary"
                    fill
                    loading={isSaving}
                    disabled={!dirty || isLoading}
                    onClick={handleSubmit}
                    data-test="page-layout-save-button"
                  >
                    Save Changes
                  </Button>
                  <Button
                    variant="danger"
                    fill
                    loading={isDeleting}
                    disabled={isLoading}
                    onClick={this.handleDeleteButtonClicked}
                    data-test="page-layout-delete-button"
                  >
                    Delete
                  </Button>
                  <Dialog
                    message={STRINGS.DIALOG.DELETE.MESSAGE}
                    title={STRINGS.DIALOG.DELETE.TITLE}
                    cancelText="No"
                    confirmText="Yes"
                    onConfirm={this.handleDelete}
                    onCancel={() => {
                      this.setState({ openDeleteDialog: false });
                    }}
                    isOpen={openDeleteDialog}
                  />
                </div>
                <Divider />
                {!!saveErrors && (
                  <div
                    className="cr-max-width-content cr-display-block-centered"
                    data-test="settings-optional-modules-alert"
                  >
                    <Alert type="danger" title={STRINGS.ERROR.SAVE_ERROR.TITLE}>
                      {get(saveErrors, 'response.data.httpCode') === '422'
                        ? STRINGS.ERROR.SAVE_CREDENTIALS_ERROR.MESSAGE
                        : STRINGS.ERROR.SAVE_ERROR.MESSAGE}
                    </Alert>
                  </div>
                )}
                {!!deleteErrors && (
                  <div
                    className="cr-max-width-content cr-display-block-centered"
                    data-test="settings-optional-modules-alert"
                  >
                    <Alert type="danger" title={STRINGS.ERROR.DELETE_ERROR.TITLE}>
                      {get(
                        deleteErrors,
                        'response.data.message',
                        STRINGS.ERROR.DELETE_ERROR.MESSAGE
                      )}
                    </Alert>
                  </div>
                )}
                <Layout>
                  <LayoutSection
                    title={STRINGS.SELECT_GATEWAY.TITLE}
                    description={STRINGS.SELECT_GATEWAY.DESCRIPTION}
                  >
                    <GatewayType
                      isLoading={isLoading}
                      gatewayTypes={availableGatewayTypes}
                      fieldName="gatewayID"
                      disabled
                    />
                  </LayoutSection>
                  <Divider />
                  <LayoutSection
                    title={STRINGS.GATEWAY_NAME.TITLE}
                    description={STRINGS.GATEWAY_NAME.DESCRIPTION}
                  >
                    <GatewayName isLoading={isLoading} fieldName="gatewayName" />
                  </LayoutSection>
                </Layout>
                <Divider />
                <LayoutSection
                  title={STRINGS.GATEWAY_CREDENTIALS.TITLE}
                  description={STRINGS.GATEWAY_CREDENTIALS.DESCRIPTION}
                >
                  <GatewayCredentials
                    isLoading={isLoading}
                    parameters={gatewayCredentialParameters}
                  />
                </LayoutSection>
                <Divider />
                <LayoutSection
                  title={STRINGS.SELECTED_SHOPS.TITLE}
                  description={STRINGS.SELECTED_SHOPS.DESCRIPTION}
                >
                  <SelectedShops isLoading={isLoading} shops={shops} gatewayConfigId={gatewayId} />
                </LayoutSection>
                <GatewayConfiguration
                  isLoading={isLoading}
                  parameters={gatewayConfigurationParameters}
                />
                <Divider />
                <LayoutSection
                  title={STRINGS.PAYMENT_MAPPING.TITLE}
                  description={STRINGS.PAYMENT_MAPPING.DESCRIPTION}
                >
                  <PaymentTypeMapping
                    paymentTypes={paymentTypes}
                    cardMappings={cardMappings}
                    isLoading={isLoading}
                  />
                </LayoutSection>
              </Fragment>
            );
          }}
        />
      </ViewError>
    );
  }
}

EditGatewayComponent.defaultProps = {
  isLoading: true,
  isErroring: false,
};

const mapStateToProps = (state, ownProps) => {
  const { match } = ownProps;
  const gatewayId = get(match, 'params.gatewayId');
  const gatewayConfig = getValue(entities.gatewayConfig, gatewayId)(state);

  return {
    gatewayConfig,
    gatewayConfigurationParameters: getGatewayConfigurationParameters(
      get(gatewayConfig, 'gatewayID')
    )(state),
    gatewayCredentialParameters: getGatewayCredentialParameters(get(gatewayConfig, 'gatewayID'))(
      state
    ),
    availableGatewayTypes: getAvailableGatewayTypes(state),
    paymentTypes: getMappablePaymentTypes(state),
    cardMappings: getGatewayCardMappings(get(gatewayConfig, 'gatewayConfigID'))(state),
    shops: getValues(entities.shop)(state),
    configuredTerminals: getCCTerminalByGatewayConfigID(get(gatewayConfig, 'gatewayConfigID'))(
      state
    ),
    isErroring: !!(
      getErrors({ entity: entities.gatewayConfig, list: false, actionType: actionTypes.LOADING })(
        state
      ) ||
      getErrors({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(state) ||
      getErrors({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
        state
      ) ||
      getErrors({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state)
    ),
    isLoading: !!(
      getProcessing({
        entity: entities.gatewayConfig,
        list: false,
        actionType: actionTypes.LOADING,
      })(state) ||
      getProcessing({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(
        state
      ) ||
      getProcessing({ entity: entities.paymentType, list: true, actionType: actionTypes.LOADING })(
        state
      ) ||
      getProcessing({ entity: entities.shop, list: true, actionType: actionTypes.LOADING })(state)
    ),
    isSaving: getProcessing({
      entity: entities.gatewayConfig,
      list: false,
      actionType: actionTypes.SAVING,
    })(state),
    saveErrors:
      getErrors({ entity: entities.gatewayConfig, list: false, actionType: actionTypes.SAVING })(
        state
      ) || getErrors({ entity: entities.shop, list: false, actionType: actionTypes.SAVING })(state),
    isDeleting: !!getProcessing({
      entity: entities.gatewayConfig,
      list: false,
      actionType: actionTypes.DELETING,
    })(state),
    deleteErrors: getErrors({
      entity: entities.gatewayConfig,
      list: false,
      actionType: actionTypes.DELETING,
    })(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { match } = ownProps;
  const gatewayId = get(match, 'params.gatewayId');

  return {
    dispatch,
    getData: () => {
      dispatch(
        getData({
          url: `/API/Account/${merchantos.account.id}/GatewayConfig/${gatewayId}.json`,
          entity: entities.gatewayConfig,
          list: false,
        })
      );
      dispatch(
        getData({
          url: `/API/Account/${merchantos.account.id}/Gateway.json`,
          entity: entities.gateway,
          list: true,
        })
      );
      dispatch(
        getData({
          url: `/API/Account/${merchantos.account.id}/PaymentType.json`,
          entity: entities.paymentType,
          list: true,
        })
      );
      dispatch(
        getData({
          url: `/API/Account/${merchantos.account.id}/Shop.json`,
          entity: entities.shop,
          list: true,
        })
      );
    },
    clearData: () => {
      dispatch(
        clearData([
          entities.gatewayConfig,
          entities.shop,
          entities.ccTerminal,
          entities.paymentType,
        ])
      );
    },
    saveData: (gatewayConfigId, payload) => {
      dispatch(
        putData({
          url: `/API/Account/${merchantos.account.id}/GatewayConfig/${gatewayConfigId}.json`,
          payload,
          entity: entities.gatewayConfig,
        })
      );
    },
    saveShopData: (shopId, payload) => {
      dispatch(
        putData({
          url: `/API/Account/${merchantos.account.id}/Shop/${shopId}.json`,
          payload,
          entity: entities.shop,
        })
      );
    },
    deleteGateway: (gatewayConfigId, onSuccess) => {
      dispatch(
        deleteData({
          url: `/API/Account/${merchantos.account.id}/GatewayConfig/${gatewayConfigId}.json`,
          entity: entities.gatewayConfig,
          list: false,
          onSuccess,
        })
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditGatewayComponent);
